import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

type Props = { title: string; children: ReactNode; onClose: () => void };

export function Dialog({ title, children, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const invokingElement =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    closeRef.current?.focus();
    return () => invokingElement?.focus();
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusableElements = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((element) => !element.hasAttribute('hidden'));
    const firstElement = focusableElements.at(0);
    const lastElement = focusableElements.at(-1);
    if (!firstElement || !lastElement) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      onKeyDown={handleKeyDown}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        tabIndex={-1}
        className="flex max-h-[85vh] max-w-md flex-col rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 id="dialog-title" className="shrink-0 text-xl font-bold">
          {title}
        </h2>
        {/* Only this middle section scrolls -- the title and Close button
            stay in view regardless of how long a caller's content is, so
            a long form or list can never trap its own submit/close
            controls off-screen (the shared version of the same
            unreachable-scrollbar bug fixed elsewhere in the app). */}
        <div className="my-4 overflow-y-auto">{children}</div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-lg bg-emerald-800 px-4 py-2 font-semibold text-white"
        >
          Close
        </button>
      </section>
    </div>
  );
}
